import React, { useEffect, useMemo, useState } from "react";
import ActivepatientApi from "../APIs/activePatientApi";

export default function PrescriptionModal({
  isOpen,
  onClose,
  mode = "create", // 'create' | 'edit'
  initialData = null, // for edit: fields from PrescriptionResponseDTO
  patientId,
  providerId,
  onSaved, // (savedPrescription) => void
}) {
  const [form, setForm] = useState({
    medicationId: "",
    frequency: "",
    durationDays: "",
    startDate: "",
    endDate: "",
    prescriptionTime: "",
    instructions: "",
  });
  const [medications, setMedications] = useState([]);
  const [medsLoading, setMedsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // Fetch medications when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setMedsLoading(true);
    ActivepatientApi.getAllMedications()
      .then((data) => setMedications(data))
      .catch((err) => console.error("Failed to load medications:", err))
      .finally(() => setMedsLoading(false));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mode === "edit" && initialData) {
      setForm({
        medicationId: initialData.medicationId ?? "",
        frequency: initialData.frequency ?? "",
        durationDays: initialData.durationDays ?? "",
        startDate: initialData.startDate ?? "",
        endDate: initialData.endDate ?? "",
        prescriptionTime: initialData.prescriptionTime ?? "",
        instructions: initialData.instructions ?? "",
      });
      setErrMsg("");
    }
    if (isOpen && mode === "create") {
      setForm({
        medicationId: "",
        frequency: "",
        durationDays: "",
        startDate: "",
        endDate: "",
        prescriptionTime: "",
        instructions: "",
      });
      setErrMsg("");
    }
  }, [isOpen, mode, initialData]);

  const title = useMemo(
    () => (mode === "edit" ? "Edit Prescription" : "Add Prescription"),
    [mode],
  );

  const allFieldsFilled =
    form.medicationId !== "" &&
    form.frequency.trim() !== "" &&
    form.durationDays !== "" &&
    form.startDate !== "" &&
    form.endDate !== "" &&
    form.prescriptionTime !== "" &&
    form.instructions.trim() !== "";

  if (!isOpen) return null;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    setSubmitting(true);
    try {
      const payload = {
        patientId,
        providerId,
        medicationId: form.medicationId ? Number(form.medicationId) : null,
        frequency: form.frequency || null,
        durationDays:
          form.durationDays !== "" ? Number(form.durationDays) : null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        prescriptionTime: form.prescriptionTime || null,
        instructions: form.instructions || null,
      };

      let saved;
      if (mode === "create") {
        saved = await ActivepatientApi.createPrescription(payload);
      } else {
        saved = await ActivepatientApi.updatePrescription(
          initialData.prescriptionId,
          payload,
        );
      }

      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const details = err?.body?.errors
        ? Object.entries(err.body.errors)
            .map(([k, v]) => `${k}: ${v}`)
            .join("\n")
        : "";
      setErrMsg(`${err.message}${details ? `\n${details}` : ""}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {errMsg && (
          <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700 whitespace-pre-line">
            {errMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium">Medication</label>
            <select
              name="medicationId"
              value={form.medicationId}
              onChange={onChange}
              className="mt-1 w-full rounded border px-3 py-2"
              required
              disabled={medsLoading}
            >
              <option value="">
                {medsLoading ? "Loading medications..." : "Select a medication"}
              </option>
              {medications.map((med) => (
                <option key={med.medicationId} value={med.medicationId}>
                  {med.name}
                  {med.form ? ` — ${med.form}` : ""}
                  {med.strength ? ` ${med.strength}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium">Frequency</label>
              <input
                type="text"
                name="frequency"
                value={form.frequency}
                onChange={onChange}
                placeholder="e.g., BID, TID, QD"
                className="mt-1 w-full rounded border px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium">
                Duration (days)
              </label>
              <input
                type="number"
                name="durationDays"
                value={form.durationDays}
                onChange={onChange}
                className="mt-1 w-full rounded border px-3 py-2"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={onChange}
                className="mt-1 w-full rounded border px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium">End Date</label>
              <input
                type="date"
                name="endDate"
                value={form.endDate}
                onChange={onChange}
                className="mt-1 w-full rounded border px-3 py-2"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium">
              Prescription Time
            </label>
            <input
              type="time"
              name="prescriptionTime"
              value={form.prescriptionTime}
              onChange={onChange}
              className="mt-1 w-full rounded border px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Instructions</label>
            <textarea
              name="instructions"
              value={form.instructions}
              onChange={onChange}
              rows={3}
              className="mt-1 w-full rounded border px-3 py-2"
              required
            />
          </div>

          {!allFieldsFilled && (
            <p className="text-sm text-amber-600">
              Please fill in all fields before saving.
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !allFieldsFilled}
              className="rounded px-4 py-2 text-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
            >
              {submitting ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
